/**
 * Created by Viacheslav Stepanian on 10.03.2026
 */

const FOG_DECORATOR_WEIGHTS = {
    RARE: { weight: 1 },
    MEDIUM: { weight: 3 },
    OFTEN: { weight: 5 },
    VERY_RARE: { weight: 5, maxCount: 1, probability: 0.25 }
};

const FogDecorations = {
    CONFIG: {
        density: 0.25,
        densityJitter: 0.05,
        jitter: {
            interior: 0.35,
            edge: 0.15,
            corner: 0.05
        },
        biomes: {
            /* exampleConfig: { decorators: { decorator_tree1: { weight: 2, probability: 0.5, maxCount: 2, size: [2, 2] }, decorator_greenrock1: { weight: 3 } } } */
            default: {
                decorators: {}
            },
            medieval: {
                decorators: {}
            },
            medieval_green: {
                decorators: {
                    decorator_birds: FOG_DECORATOR_WEIGHTS.VERY_RARE,
                    decorator_greenrock1: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_greenrock2: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greenrock3: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_greenruins1: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_greentree1: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greentree2: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greentree3: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_greentree4: FOG_DECORATOR_WEIGHTS.MEDIUM
                }
            },
            medieval_blue: {
                decorators: {
                    decorator_birds: FOG_DECORATOR_WEIGHTS.VERY_RARE,
                    decorator_greenrock1: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_greenrock2: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greenrock3: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_greenruins1: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_greentree1: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greentree2: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greentree3: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_greentree4: FOG_DECORATOR_WEIGHTS.MEDIUM
                }
            },
            medieval_purple: {
                decorators: {
                    decorator_birds: FOG_DECORATOR_WEIGHTS.VERY_RARE,
                    decorator_greenrock1: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_greenrock2: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greenrock3: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_greenruins1: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_greentree1: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greentree2: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_greentree3: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_greentree4: FOG_DECORATOR_WEIGHTS.MEDIUM
                }
            },
            medieval_ice: {
                decorators: {
                    decorator_icerock1: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_icerock2: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_icerock3: { ...FOG_DECORATOR_WEIGHTS.RARE, size: [1, 2] },
                    decorator_icerock4: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_icetree1: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_icetree2: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_icetree3: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_icetree4: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_icetree6: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_icewind1: FOG_DECORATOR_WEIGHTS.VERY_RARE
                }
            },
            medieval_sand: {
                decorators: {
                    decorator_sandrock1: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_sandrock2: FOG_DECORATOR_WEIGHTS.RARE,
                    decorator_sandrock3: { ...FOG_DECORATOR_WEIGHTS.RARE, size: [2, 1] },
                    decorator_sandrock4: { ...FOG_DECORATOR_WEIGHTS.RARE, size: [2, 2] },
                    decorator_sandtree1: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_sandtree2: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_sandtree3: FOG_DECORATOR_WEIGHTS.OFTEN,
                    decorator_sandtree4: FOG_DECORATOR_WEIGHTS.MEDIUM,
                    decorator_sandwind1: FOG_DECORATOR_WEIGHTS.VERY_RARE
                }
            }
        }
    },

    getBiomeConfig: function (fogType, theme) {
        const biomes = this.CONFIG.biomes;
        return biomes[fogType] || biomes[theme] || biomes.default || {};
    },

    getDecoratorTypes: function (bundleName) {
        const bundle = bundles[bundleName];
        if (!bundle) {
            return [];
        }
        const fromJsons = bundle.jsons ? Object.keys(bundle.jsons).filter((key) => key.startsWith("decorator_")) : [];
        const fromFrames = bundle.frames ? Object.keys(bundle.frames).filter((key) => key.startsWith("decorator_")) : [];
        return fromJsons.concat(fromFrames);
    },

    getAvailableTiles: function (fogBlock) {
        return fogBlock.tiles.filter((tile) => !tile.isPointOfInterest());
    },

    decorationJitter: function (rng, tile, jitterConfig) {
        const cell = cleverapps.styles.Map2dView.cell;
        const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];
        const openDirs = dirs.filter(([dx, dy]) => !Map2d.currentMap.getFog(tile.x + dx, tile.y + dy));

        if (openDirs.length === 0) {
            const scale = jitterConfig.interior;
            return {
                dx: rng.nextDouble() * scale * cell.width,
                dy: rng.nextDouble() * scale * cell.height
            };
        }

        let inwardX = 0, inwardY = 0;
        openDirs.forEach(([dx, dy]) => {
            inwardX -= dx;
            inwardY -= dy;
        });

        const scale = openDirs.length >= 2 ? jitterConfig.corner : jitterConfig.edge;
        return {
            dx: rng.nextDouble() * scale * Math.max(0, inwardX) * cell.width,
            dy: rng.nextDouble() * scale * Math.max(0, inwardY) * cell.height
        };
    },

    generate: function (fogBlock) {
        const headTile = fogBlock.tiles.find((t) => t.isHead);
        if (!headTile) {
            return [];
        }

        const fogBundle = headTile.getFogBundle();
        const fogType = headTile.getFogKey();

        const terrainType = Map2d.currentMap.getTerrainType(headTile.x, headTile.y);
        const tileMeta = (bundles[terrainType.name] || bundles[terrainType.defaultTiles]).meta;
        const theme = tileMeta.fog;
        const biomeConfig = this.getBiomeConfig(fogType, theme);
        const decoratorsConfig = biomeConfig.decorators || {};

        const types = this.getDecoratorTypes(fogBundle.name);
        if (!types.length) {
            return [];
        }

        const available = this.getAvailableTiles(fogBlock);
        if (!available.length) {
            return [];
        }

        const rng = new PseudoRandom(fogBlock.id);
        const shuffledTiles = rng.shuffle(available);
        const jitter = Math.round((rng.nextDouble() - 0.5) * available.length * this.CONFIG.densityJitter);
        const targetCoverage = Math.min(available.length, Math.max(0, Math.round(available.length * this.CONFIG.density) + jitter));

        const multiTypes = [], singleTypes = [];
        types.forEach((t) => {
            const [sw, sh] = (decoratorsConfig[t] || FOG_DECORATOR_WEIGHTS.MEDIUM).size || [1, 1];
            (sw > 1 || sh > 1 ? multiTypes : singleTypes).push(t);
        });

        const totalWeight = types.reduce((sum, t) => sum + (decoratorsConfig[t] || FOG_DECORATOR_WEIGHTS.MEDIUM).weight, 0);

        const composed = rng.shuffle(
            types.flatMap((t) => {
                const cfg = decoratorsConfig[t] || FOG_DECORATOR_WEIGHTS.MEDIUM;
                if (cfg.probability !== undefined && rng.nextDouble() >= cfg.probability) {
                    return [];
                }
                const exact = cfg.weight * targetCoverage / totalWeight;
                const count = Math.floor(exact) + (rng.nextDouble() < exact % 1 ? 1 : 0);
                return Array(count).fill(t);
            })
        );

        const availableSet = new Set(available.map((t) => `${t.x}_${t.y}`));
        const occupiedSet = new Set();
        const typeCounts = {};
        const result = {};
        let coveredTiles = 0;

        const placeFromPool = (pool) => {
            pool.forEach((image) => {
                if (coveredTiles >= targetCoverage) {
                    return;
                }

                const cfg = decoratorsConfig[image] || FOG_DECORATOR_WEIGHTS.MEDIUM;
                const [sw, sh] = cfg.size || [1, 1];

                const tile = shuffledTiles.find((t) => !occupiedSet.has(`${t.x}_${t.y}`)
                    && this.checkRect(t.x, t.y, sw, sh, availableSet, occupiedSet));

                if (!tile) {
                    return;
                }

                const maxCount = cfg.maxCount;
                if (maxCount) {
                    if ((typeCounts[image] || 0) >= maxCount) {
                        return;
                    }
                    typeCounts[image] = (typeCounts[image] || 0) + 1;
                }

                for (let dx = 0; dx < sw; dx++) {
                    for (let dy = 0; dy < sh; dy++) {
                        occupiedSet.add(`${tile.x + dx}_${tile.y + dy}`);
                    }
                }
                coveredTiles += sw * sh;

                const anchorX = tile.x;
                const anchorY = tile.y + sh - 1;
                const decorationJitter = sw === 1 && sh === 1
                    ? this.decorationJitter(rng, tile, this.CONFIG.jitter)
                    : { dx: 0, dy: 0 };

                result[`${anchorX}_${anchorY}`] = {
                    tileX: anchorX,
                    tileY: anchorY,
                    bundle: fogBundle.name,
                    image,
                    sw,
                    sh,
                    dx: decorationJitter.dx,
                    dy: decorationJitter.dy
                };
            });
        };

        placeFromPool(composed.filter((t) => multiTypes.includes(t)));
        placeFromPool(composed.filter((t) => singleTypes.includes(t)));

        return result;
    },

    checkRect: function (x, y, w, h, availableSet, occupiedSet) {
        for (let dx = 0; dx < w; dx++) {
            for (let dy = 0; dy < h; dy++) {
                const k = `${x + dx}_${y + dy}`;
                if (!availableSet.has(k) || occupiedSet.has(k)) {
                    return false;
                }
            }
        }
        return true;
    }
};

FogDecorations.enabled = false;
